import React, { useState, useMemo, useEffect } from 'react'
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  createColumnHelper,
  type SortingState,
  type ColumnDef,
} from '@tanstack/react-table'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { ChevronDown, ChevronRight, AlertCircle, ArrowUpDown } from 'lucide-react'
import { StatusBadge } from './StatusBadge'
import type {
  ProcessedUnit,
  UnitGroup,
  RenewalStatus,
  RevenueStreamId,
  DisplayStatus,
  LeaseDisplayRow,
  TransientAggRow,
} from '@/lib/reports/space-yield/types'
import { toDisplayStatus } from '@/lib/reports/space-yield/types'
import { formatCurrency, formatYieldPerSqft } from '@/lib/reports/space-yield/compute'

interface UnitDetailTableProps {
  groups: UnitGroup[]
  hasTransientData: boolean
  hasFuelData: boolean
  groupBy: string
  visibleColumns: string[]
  streams?: RevenueStreamId[]
  /** Ref map for scrolling to groups from building bars */
  groupRefs?: React.MutableRefObject<Record<string, HTMLTableRowElement | null>>
  /** When set, auto-expand this group key (from building bar click) */
  expandGroupKey?: string | null
}

const columnHelper = createColumnHelper<ProcessedUnit>()

// ─── Column Registry ────────────────────────────────────────────────

type ColMeta = { align?: 'right' | 'left'; hero?: boolean }

function buildAllColumns(): Record<string, ColumnDef<ProcessedUnit, unknown>> {
  return {
    // Core columns (always visible)
    unit: columnHelper.accessor('unit_code', {
      id: 'unit',
      header: 'Unit',
      cell: (info) => <span className="font-medium text-slate-900">{info.getValue()}</span>,
      size: 100,
    }) as ColumnDef<ProcessedUnit, unknown>,

    status: columnHelper.accessor('status', {
      id: 'status',
      header: 'Status',
      cell: (info) => <StatusBadge status={info.getValue()} />,
      size: 140,
    }) as ColumnDef<ProcessedUnit, unknown>,

    yield: columnHelper.accessor('yield_per_sqft', {
      id: 'yield',
      header: 'Revenue/Sqft',
      cell: (info) => {
        const row = info.row.original
        const val = info.getValue()
        const isIdle = row.status === 'idle' || row.status === 'maintenance'
        let bgClass = ''
        if (isIdle) {
          bgClass = 'bg-slate-100'
        } else if (val === 0) {
          bgClass = 'bg-red-50'
        } else if (row.rate_gap_pct != null) {
          bgClass = row.rate_gap_pct >= 100 ? 'bg-emerald-50' :
                    row.rate_gap_pct >= 50 ? 'bg-amber-50' : 'bg-red-50'
        }
        return (
          <span className={`font-bold text-slate-900 font-mono inline-block w-full text-right px-2 py-0.5 rounded ${bgClass}`}>
            {formatYieldPerSqft(val)}
          </span>
        )
      },
      size: 120,
      meta: { align: 'right', hero: true } satisfies ColMeta,
    }) as ColumnDef<ProcessedUnit, unknown>,

    // Default columns (hideable)
    sqft: columnHelper.accessor('sqft', {
      id: 'sqft',
      header: 'Sqft',
      cell: (info) => <span className="text-slate-600">{info.getValue().toLocaleString()}</span>,
      size: 100,
      meta: { align: 'right' } satisfies ColMeta,
    }) as ColumnDef<ProcessedUnit, unknown>,

    tenant: columnHelper.accessor('tenant_name', {
      id: 'tenant',
      header: 'Tenant',
      cell: (info) => (
        <span className="text-slate-700 font-medium">{info.getValue() || '\u2014'}</span>
      ),
      size: 160,
    }) as ColumnDef<ProcessedUnit, unknown>,

    lease_rate: columnHelper.accessor('lease_rate', {
      id: 'lease_rate',
      header: 'Lease Rate',
      cell: (info) => (
        <span className="text-slate-600 font-mono">
          {info.getValue() ? formatCurrency(info.getValue()!) : '\u2014'}
        </span>
      ),
      size: 120,
      meta: { align: 'right' } satisfies ColMeta,
    }) as ColumnDef<ProcessedUnit, unknown>,

    trans_rev: columnHelper.accessor('transient_revenue', {
      id: 'trans_rev',
      header: 'Trans. Rev',
      cell: (info) => (
        <span className="text-blue-600 font-mono font-medium">
          {info.getValue() ? formatCurrency(info.getValue()!, 0) : '\u2014'}
        </span>
      ),
      size: 120,
      meta: { align: 'right' } satisfies ColMeta,
    }) as ColumnDef<ProcessedUnit, unknown>,

    contract_end: columnHelper.accessor('lease_end', {
      id: 'contract_end',
      header: 'Contract End',
      cell: (info) => {
        const row = info.row.original
        if (row.lease_type === 'month_to_month') {
          return <span className="text-purple-600 text-sm font-medium">M2M</span>
        }
        if (!info.getValue()) return <span className="text-slate-400">{'\u2014'}</span>
        return (
          <div>
            <span className="text-slate-500 text-sm">{info.getValue()}</span>
            {row.days_to_expiry !== undefined && row.days_to_expiry <= 90 && (
              <div className="text-xs text-rose-500 font-medium flex items-center gap-1 mt-0.5">
                <AlertCircle className="w-3 h-3" />
                {row.days_to_expiry} days
              </div>
            )}
          </div>
        )
      },
      size: 120,
    }) as ColumnDef<ProcessedUnit, unknown>,

    // Extended Yield columns
    annual_value: columnHelper.accessor('annual_value', {
      id: 'annual_value',
      header: 'Annual Value',
      cell: (info) => (
        <span className="text-slate-700 font-mono">
          {info.getValue() ? formatCurrency(info.getValue()!, 0) : '\u2014'}
        </span>
      ),
      size: 120,
      meta: { align: 'right' } satisfies ColMeta,
    }) as ColumnDef<ProcessedUnit, unknown>,

    rate_gap: columnHelper.accessor('rate_gap_pct', {
      id: 'rate_gap',
      header: 'Rate Gap %',
      cell: (info) => {
        const val = info.getValue()
        if (val == null) return <span className="text-slate-400">{'\u2014'}</span>
        const color =
          val < 80 ? 'text-red-600' :
          val < 95 ? 'text-orange-600' :
          val > 110 ? 'text-emerald-600' : 'text-slate-700'
        return <span className={`font-mono font-medium ${color}`}>{val.toFixed(0)}%</span>
      },
      size: 100,
      meta: { align: 'right' } satisfies ColMeta,
    }) as ColumnDef<ProcessedUnit, unknown>,

    vacancy_cost: columnHelper.accessor('vacancy_cost', {
      id: 'vacancy_cost',
      header: 'Vacancy Cost',
      cell: (info) => (
        <span className="text-red-600 font-mono">
          {info.getValue() ? formatCurrency(info.getValue()!, 0) : '\u2014'}
        </span>
      ),
      size: 120,
      meta: { align: 'right' } satisfies ColMeta,
    }) as ColumnDef<ProcessedUnit, unknown>,

    trans_rate_night: columnHelper.accessor('trans_rate_per_night', {
      id: 'trans_rate_night',
      header: 'Trans. Rate/Night',
      cell: (info) => (
        <span className="text-blue-600 font-mono">
          {info.getValue() ? formatCurrency(info.getValue()!, 0) : '\u2014'}
        </span>
      ),
      size: 130,
      meta: { align: 'right' } satisfies ColMeta,
    }) as ColumnDef<ProcessedUnit, unknown>,

    utilization: columnHelper.accessor('utilization_pct', {
      id: 'utilization',
      header: 'Utilization %',
      cell: (info) => {
        const val = info.getValue()
        if (val == null) return <span className="text-slate-400">{'\u2014'}</span>
        const barColor =
          val >= 80 ? 'bg-emerald-500' :
          val >= 50 ? 'bg-amber-400' : 'bg-red-400'
        return (
          <div className="flex items-center gap-2">
            <div className="w-12 h-2 bg-slate-200 rounded-full overflow-hidden">
              <div className={`h-full rounded-full ${barColor}`} style={{ width: `${val}%` }} />
            </div>
            <span className="text-xs font-mono text-slate-600">{val}%</span>
          </div>
        )
      },
      size: 130,
    }) as ColumnDef<ProcessedUnit, unknown>,

    renewal: columnHelper.accessor('renewal_status', {
      id: 'renewal',
      header: 'Renewal',
      cell: (info) => {
        const val = info.getValue() as RenewalStatus | undefined
        if (!val || val === '\u2014') return <span className="text-slate-400">{'\u2014'}</span>
        const variant: Record<string, string> = {
          'Auto-Renew': 'bg-emerald-100 text-emerald-700',
          'Contacted': 'bg-blue-100 text-blue-700',
          'Not Started': 'bg-yellow-100 text-yellow-700',
        }
        return (
          <Badge variant="outline" className={`text-xs ${variant[val] || ''}`}>
            {val}
          </Badge>
        )
      },
      size: 120,
    }) as ColumnDef<ProcessedUnit, unknown>,

    days_to_expiry: columnHelper.accessor('days_to_expiry', {
      id: 'days_to_expiry',
      header: 'Days to Expiry',
      cell: (info) => {
        const val = info.getValue()
        if (val == null) return <span className="text-slate-400">{'\u2014'}</span>
        const color =
          val <= 30 ? 'text-red-600 font-bold' :
          val <= 60 ? 'text-orange-600 font-medium' :
          val <= 90 ? 'text-yellow-600' : 'text-slate-600'
        return <span className={`font-mono ${color}`}>{val}</span>
      },
      size: 110,
      meta: { align: 'right' } satisfies ColMeta,
    }) as ColumnDef<ProcessedUnit, unknown>,

    escalation: columnHelper.accessor('escalation_pct', {
      id: 'escalation',
      header: 'Escalation %',
      cell: (info) => {
        const pct = info.getValue()
        const row = info.row.original
        if (pct == null || row.escalation_type === 'none') {
          return <span className="text-slate-400">{'\u2014'}</span>
        }
        const typeLabel = row.escalation_type === 'cpi' ? 'CPI' : 'Fixed'
        return (
          <span className="font-mono text-slate-700">
            {pct}% <span className="text-xs text-slate-400">({typeLabel})</span>
          </span>
        )
      },
      size: 120,
      meta: { align: 'right' } satisfies ColMeta,
    }) as ColumnDef<ProcessedUnit, unknown>,

    // Fuel columns
    fuel_rev: columnHelper.accessor('fuel_revenue', {
      id: 'fuel_rev',
      header: 'Fuel Revenue',
      cell: (info) => (
        <span className="text-amber-600 font-mono font-medium">
          {info.getValue() ? formatCurrency(info.getValue()!, 0) : '\u2014'}
        </span>
      ),
      size: 120,
      meta: { align: 'right' } satisfies ColMeta,
    }) as ColumnDef<ProcessedUnit, unknown>,

    fuel_commitment: columnHelper.accessor('fuel_commitment_gallons', {
      id: 'fuel_commitment',
      header: 'Fuel Commitment',
      cell: (info) => {
        const row = info.row.original
        if (!info.getValue()) return <span className="text-slate-400">{'\u2014'}</span>
        const delivered = row.fuel_delivered_gallons ?? 0
        const committed = info.getValue()!
        const pct = row.fuel_commitment_pct ?? 0
        const barColor =
          pct >= 90 ? 'bg-emerald-500' :
          pct >= 60 ? 'bg-amber-400' : 'bg-red-400'
        return (
          <div className="flex items-center gap-2">
            <div className="w-10 h-2 bg-slate-200 rounded-full overflow-hidden">
              <div className={`h-full rounded-full ${barColor}`} style={{ width: `${Math.min(100, pct)}%` }} />
            </div>
            <span className="text-xs font-mono text-amber-700">{delivered}/{committed}</span>
          </div>
        )
      },
      size: 150,
    }) as ColumnDef<ProcessedUnit, unknown>,

    total_value: columnHelper.accessor('total_tenant_value', {
      id: 'total_value',
      header: 'Total Value',
      cell: (info) => (
        <span className="text-slate-900 font-mono font-bold">
          {info.getValue() ? formatCurrency(info.getValue()!, 0) : '\u2014'}
        </span>
      ),
      size: 120,
      meta: { align: 'right' } satisfies ColMeta,
    }) as ColumnDef<ProcessedUnit, unknown>,

    total_value_per_sqft: columnHelper.accessor('total_value_per_sqft', {
      id: 'total_value_per_sqft',
      header: 'Total Value/Sqft',
      cell: (info) => (
        <span className="text-slate-900 font-mono font-medium">
          {info.getValue() ? formatYieldPerSqft(info.getValue()!) : '\u2014'}
        </span>
      ),
      size: 130,
      meta: { align: 'right' } satisfies ColMeta,
    }) as ColumnDef<ProcessedUnit, unknown>,
  }
}

// Stable column order (determines rendering order)
const COLUMN_ORDER = [
  'unit', 'sqft', 'status', 'tenant', 'lease_rate', 'trans_rev', 'fuel_rev', 'yield',
  'total_value', 'total_value_per_sqft', 'fuel_commitment',
  'contract_end', 'annual_value', 'rate_gap', 'vacancy_cost',
  'trans_rate_night', 'utilization', 'renewal', 'days_to_expiry', 'escalation',
]

const FUEL_COLUMN_IDS = new Set(['fuel_rev', 'fuel_commitment', 'total_value', 'total_value_per_sqft'])

export function UnitDetailTable({ groups, hasTransientData, hasFuelData, groupBy, visibleColumns, streams, groupRefs, expandGroupKey }: UnitDetailTableProps) {
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({})
  const [sorting, setSorting] = useState<SortingState>([])

  // Auto-expand group when triggered from building bar click
  useEffect(() => {
    if (expandGroupKey) {
      setExpandedGroups((prev) => ({ ...prev, [expandGroupKey]: true }))
    }
  }, [expandGroupKey])

  const isBuildingMode = groupBy === 'building'

  const toggleGroup = (key: string) => {
    setExpandedGroups((prev) => ({ ...prev, [key]: prev[key] === true ? false : true }))
  }

  const allUnits = useMemo(() => groups.flatMap((g) => g.units), [groups])

  const columns = useMemo(() => {
    const registry = buildAllColumns()
    const colSet = new Set(visibleColumns)

    // Auto-hide trans_rev if no transient data
    if (!hasTransientData) colSet.delete('trans_rev')

    // Auto-hide fuel columns if no fuel data
    if (!hasFuelData) {
      for (const id of FUEL_COLUMN_IDS) colSet.delete(id)
    }

    // Auto-hide columns based on disabled revenue streams
    if (streams) {
      if (!streams.includes('lease')) colSet.delete('lease_rate')
      if (!streams.includes('transient')) {
        colSet.delete('trans_rev')
        colSet.delete('trans_rate_night')
        colSet.delete('utilization')
      }
      if (!streams.includes('fuel')) {
        for (const id of FUEL_COLUMN_IDS) colSet.delete(id)
      }
    }

    return COLUMN_ORDER
      .filter((id) => colSet.has(id))
      .map((id) => registry[id])
      .filter(Boolean) as ColumnDef<ProcessedUnit, unknown>[]
  }, [visibleColumns, hasTransientData, hasFuelData, streams])

  const colCount = columns.length

  const table = useReactTable({
    data: allUnits,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  // Grand totals
  const grandTotals = useMemo(() => {
    const totalSqft = allUnits.reduce((s, u) => s + u.sqft, 0)
    const leaseTotal = allUnits.reduce((s, u) => s + (u.lease_rate || 0), 0)
    const transTotal = allUnits.reduce((s, u) => s + (u.transient_revenue || 0), 0)
    const fuelTotal = allUnits.reduce((s, u) => s + (u.fuel_revenue || 0), 0)
    const totalRev = leaseTotal + transTotal
    const totalTenantValue = totalRev + fuelTotal
    const avgYield = totalSqft > 0 ? totalRev / totalSqft : 0
    const totalValuePerSqft = totalSqft > 0 ? totalTenantValue / totalSqft : 0
    const annualTotal = allUnits.reduce((s, u) => s + (u.annual_value || 0), 0)
    const vacancyTotal = allUnits.reduce((s, u) => s + (u.vacancy_cost || 0), 0)
    return { totalSqft, leaseTotal, transTotal, fuelTotal, totalRev, totalTenantValue, avgYield, totalValuePerSqft, annualTotal, vacancyTotal }
  }, [allUnits])

  if (groups.length === 0) {
    return (
      <div className="rounded-md border bg-white shadow-sm p-12 text-center">
        <p className="text-slate-500 text-lg">No units match the current filters</p>
        <p className="text-slate-400 text-sm mt-2">Try adjusting the space type or segment filters</p>
      </div>
    )
  }

  const colSet = new Set(visibleColumns)
  const showCol = (id: string) => {
    if (id === 'trans_rev') return colSet.has(id) && hasTransientData
    if (FUEL_COLUMN_IDS.has(id)) return colSet.has(id) && hasFuelData
    // Stream-based auto-hide
    if (streams) {
      if (id === 'lease_rate' && !streams.includes('lease')) return false
      if ((id === 'trans_rev' || id === 'trans_rate_night' || id === 'utilization') && !streams.includes('transient')) return false
      if (FUEL_COLUMN_IDS.has(id) && !streams.includes('fuel')) return false
    }
    return colSet.has(id)
  }

  return (
    <div className="rounded-md border bg-white shadow-sm overflow-hidden">
      <Table>
        <TableHeader className="bg-slate-50">
          <TableRow>
            {table.getHeaderGroups()[0]?.headers.map((header) => {
              const meta = header.column.columnDef.meta as ColMeta | undefined
              const isHero = meta?.hero
              return (
                <TableHead
                  key={header.id}
                  className={`
                    ${meta?.align === 'right' ? 'text-right' : ''}
                    ${isHero ? 'font-bold text-slate-900 border-l border-r bg-slate-100/50' : 'font-medium text-slate-700'}
                    cursor-pointer select-none
                  `}
                  style={{ width: header.getSize() }}
                  onClick={header.column.getToggleSortingHandler()}
                >
                  <div className={`flex items-center gap-1 ${meta?.align === 'right' ? 'justify-end' : ''}`}>
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    <ArrowUpDown className="w-3 h-3 text-slate-400" />
                  </div>
                </TableHead>
              )
            })}
          </TableRow>
        </TableHeader>

        <TableBody>
          {groups.length === 1 && groupBy === 'none' ? (
            // No grouping — flat list
            groups[0].units.map((unit) => (
              <UnitRow key={unit.unit_id} unit={unit} showCol={showCol} />
            ))
          ) : isBuildingMode ? (
            // ── Building mode: two-tier table ──
            groups.map((group) => {
              const isExpanded = expandedGroups[group.key] === true // Default: collapsed
              return (
                <React.Fragment key={group.key}>
                  <BuildingRow
                    group={group}
                    isExpanded={isExpanded}
                    showCol={showCol}
                    onToggle={() => toggleGroup(group.key)}
                    groupRefs={groupRefs}
                  />
                  {isExpanded &&
                    group.units.map((unit) => (
                      <UnitRow key={unit.unit_id} unit={unit} showCol={showCol} indented />
                    ))}
                </React.Fragment>
              )
            })
          ) : (
            // ── Other group modes: colSpan header + unit rows ──
            groups.map((group) => {
              const isExpanded = expandedGroups[group.key] !== false // Default: expanded
              return (
                <React.Fragment key={group.key}>
                  <TableRow
                    ref={(el) => {
                      if (groupRefs) groupRefs.current[group.key] = el
                    }}
                    className="bg-slate-50 hover:bg-slate-100 cursor-pointer font-medium"
                    onClick={() => toggleGroup(group.key)}
                  >
                    <TableCell colSpan={colCount} className="p-2 pl-4">
                      <div className="flex items-center gap-2">
                        {isExpanded ? (
                          <ChevronDown className="w-4 h-4 text-slate-500" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-slate-500" />
                        )}
                        <span className="text-slate-800 font-semibold">{group.label}</span>
                        <span className="text-slate-500 font-normal text-sm ml-2">
                          {group.total_units} units · {group.total_sqft.toLocaleString()} sqft · {group.occupancy_pct}% occ. ({group.occupied_sqft.toLocaleString()}/{group.total_sqft.toLocaleString()} sqft)
                        </span>
                        <span className="ml-auto text-slate-600 text-sm font-medium mr-4">
                          {formatCurrency(group.total_revenue)}/mo · {formatYieldPerSqft(group.avg_yield)}/sqft avg
                          <span className="text-slate-400 ml-2">
                            (Lease: {formatCurrency(group.lease_revenue)} · Trans: {formatCurrency(group.transient_revenue)})
                          </span>
                        </span>
                      </div>
                    </TableCell>
                  </TableRow>

                  {isExpanded &&
                    group.units.map((unit) => (
                      <UnitRow key={unit.unit_id} unit={unit} showCol={showCol} />
                    ))}
                </React.Fragment>
              )
            })
          )}

          {/* Grand Total Footer */}
          <GrandTotalRow
            allUnits={allUnits}
            grandTotals={grandTotals}
            showCol={showCol}
          />
        </TableBody>
      </Table>
    </div>
  )
}

// ─── Building Status Summary ─────────────────────────────────────────

const DISPLAY_STATUS_ORDER: DisplayStatus[] = ['Leased', 'M2M', 'Transient', 'Idle', 'No Contract']

function buildingStatusSummary(units: ProcessedUnit[]): string {
  const counts = new Map<DisplayStatus, number>()
  for (const u of units) {
    const ds = toDisplayStatus(u.status)
    counts.set(ds, (counts.get(ds) || 0) + 1)
  }
  return DISPLAY_STATUS_ORDER
    .filter((ds) => (counts.get(ds) || 0) > 0)
    .map((ds) => `${counts.get(ds)} ${ds.toLowerCase()}`)
    .join(' · ')
}

// ─── Building Row (proper columns, not colSpan) ─────────────────────

function BuildingRow({
  group,
  isExpanded,
  showCol,
  onToggle,
  groupRefs,
}: {
  group: UnitGroup
  isExpanded: boolean
  showCol: (id: string) => boolean
  onToggle: () => void
  groupRefs?: React.MutableRefObject<Record<string, HTMLTableRowElement | null>>
}) {
  const annualValue = group.total_revenue * 12

  return (
    <TableRow
      ref={(el) => {
        if (groupRefs) groupRefs.current[group.key] = el
      }}
      className="bg-slate-50 hover:bg-slate-100 cursor-pointer font-semibold"
      onClick={onToggle}
    >
      {showCol('unit') && (
        <TableCell className="p-2 pl-4">
          <div className="flex items-center gap-2">
            {isExpanded ? (
              <ChevronDown className="w-4 h-4 text-slate-500 shrink-0" />
            ) : (
              <ChevronRight className="w-4 h-4 text-slate-500 shrink-0" />
            )}
            <span className="text-slate-800">{group.label}</span>
          </div>
        </TableCell>
      )}
      {showCol('sqft') && (
        <TableCell className="text-right text-slate-700 font-mono">
          {group.total_sqft.toLocaleString()}
        </TableCell>
      )}
      {showCol('status') && (
        <TableCell>
          <span className="text-xs text-slate-600">
            {buildingStatusSummary(group.units)}
          </span>
        </TableCell>
      )}
      {showCol('tenant') && (
        <TableCell className="text-slate-600 text-sm">
          <div className="flex items-center gap-2">
            <span>{group.client_summary}</span>
            {group.idle_count > 0 && (
              <Badge variant="outline" className="text-[10px] text-slate-500 border-slate-300 font-normal">
                {group.idle_count} idle
              </Badge>
            )}
          </div>
        </TableCell>
      )}
      {showCol('lease_rate') && (
        <TableCell className="text-right text-slate-700 font-mono">
          {group.lease_revenue > 0 ? formatCurrency(group.lease_revenue) : '\u2014'}
        </TableCell>
      )}
      {showCol('trans_rev') && (
        <TableCell className="text-right text-blue-600 font-mono font-medium">
          {group.transient_revenue > 0 ? formatCurrency(group.transient_revenue, 0) : '\u2014'}
        </TableCell>
      )}
      {showCol('fuel_rev') && (
        <TableCell className="text-right text-amber-600 font-mono font-medium">
          {group.fuel_revenue > 0 ? formatCurrency(group.fuel_revenue, 0) : '\u2014'}
        </TableCell>
      )}
      {showCol('yield') && (
        <TableCell className="text-right font-bold text-slate-900 bg-slate-100/50 border-l border-r font-mono">
          {formatYieldPerSqft(group.avg_yield)}
        </TableCell>
      )}
      {showCol('total_value') && (
        <TableCell className="text-right text-slate-900 font-mono font-bold">
          {group.total_tenant_value > 0 ? formatCurrency(group.total_tenant_value, 0) : '\u2014'}
        </TableCell>
      )}
      {showCol('total_value_per_sqft') && (
        <TableCell className="text-right text-slate-900 font-mono font-medium">
          {group.total_sqft > 0 && group.total_tenant_value > 0
            ? formatYieldPerSqft(group.total_tenant_value / group.total_sqft)
            : '\u2014'}
        </TableCell>
      )}
      {showCol('fuel_commitment') && <TableCell className="text-slate-400">{'\u2014'}</TableCell>}
      {showCol('contract_end') && (
        <TableCell className="text-slate-500 text-sm">
          {group.earliest_lease_end ? (
            <div>
              {group.earliest_lease_end}
              {group.earliest_days_to_expiry !== undefined && group.earliest_days_to_expiry <= 90 && (
                <div className="text-xs text-rose-500 font-medium flex items-center gap-1 mt-0.5">
                  <AlertCircle className="w-3 h-3" />
                  {group.earliest_days_to_expiry}d
                </div>
              )}
            </div>
          ) : (
            <span className="text-slate-400">{'\u2014'}</span>
          )}
        </TableCell>
      )}
      {showCol('annual_value') && (
        <TableCell className="text-right text-slate-700 font-mono">
          {annualValue > 0 ? formatCurrency(annualValue, 0) : '\u2014'}
        </TableCell>
      )}
      {showCol('rate_gap') && <TableCell className="text-slate-400">{'\u2014'}</TableCell>}
      {showCol('vacancy_cost') && (
        <TableCell className="text-right text-red-600 font-mono">
          {(() => {
            const total = group.units.reduce((s, u) => s + (u.vacancy_cost || 0), 0)
            return total > 0 ? formatCurrency(total, 0) : '\u2014'
          })()}
        </TableCell>
      )}
      {showCol('trans_rate_night') && <TableCell className="text-slate-400">{'\u2014'}</TableCell>}
      {showCol('utilization') && <TableCell className="text-slate-400">{'\u2014'}</TableCell>}
      {showCol('renewal') && <TableCell className="text-slate-400">{'\u2014'}</TableCell>}
      {showCol('days_to_expiry') && <TableCell className="text-slate-400">{'\u2014'}</TableCell>}
      {showCol('escalation') && <TableCell className="text-slate-400">{'\u2014'}</TableCell>}
    </TableRow>
  )
}

// ─── Building Sub-Rows (expanded content) ───────────────────────────

// Retained for the two-tier building sub-rows feature (see `enrichBuildingGroups`
// in lib/reports/space-yield/compute.ts). Exported so it stays part of the module
// API while the feature is unwired, rather than being dropped as dead code.
export function BuildingSubRows({
  group,
  showCol,
  colCount,
}: {
  group: UnitGroup
  showCol: (id: string) => boolean
  colCount: number
}) {
  // If sub_rows are populated (shared building logic ran), use them
  if (group.sub_rows.length > 0) {
    return (
      <>
        {group.sub_rows.map((row, i) => {
          switch (row.kind) {
            case 'section_header':
              return (
                <SectionHeaderRow
                  key={`sh-${i}`}
                  label={row.label}
                  detail={row.detail}
                  colCount={colCount}
                />
              )
            case 'lease':
              return <LeaseSubRow key={row.data.lease_id} lease={row.data} showCol={showCol} />
            case 'transient_agg':
              return <TransientAggSubRow key={`tagg-${i}`} agg={row.data} showCol={showCol} />
            case 'unit':
              return <UnitRow key={row.data.unit_id} unit={row.data} showCol={showCol} indented />
          }
        })}
      </>
    )
  }

  // Fallback: no sub_rows computed (non-shared building) — show plain unit rows
  return (
    <>
      {group.units.map((unit) => (
        <UnitRow key={unit.unit_id} unit={unit} showCol={showCol} indented />
      ))}
    </>
  )
}

// ─── Section Header Row ─────────────────────────────────────────────

function SectionHeaderRow({
  label,
  detail,
  colCount,
}: {
  label: string
  detail?: string
  colCount: number
}) {
  return (
    <TableRow className="bg-slate-50/50">
      <TableCell colSpan={colCount} className="py-1.5 pl-10 text-xs text-slate-500 font-medium tracking-wide">
        <span className="text-slate-400 mr-1">{'──'}</span>
        {label}
        {detail && <span className="text-slate-400 ml-2 font-normal">{detail}</span>}
        <span className="text-slate-400 ml-1">{'──'}</span>
      </TableCell>
    </TableRow>
  )
}

// ─── Lease Sub-Row ──────────────────────────────────────────────────

function LeaseSubRow({
  lease,
  showCol,
}: {
  lease: LeaseDisplayRow
  showCol: (id: string) => boolean
}) {
  return (
    <TableRow className="hover:bg-slate-50/50">
      {showCol('unit') && (
        <TableCell className="pl-10 font-medium text-slate-700 text-sm">{lease.unit_code}</TableCell>
      )}
      {showCol('sqft') && (
        <TableCell className="text-right text-slate-500 text-sm">{lease.sqft.toLocaleString()}</TableCell>
      )}
      {showCol('status') && (
        <TableCell><StatusBadge status={lease.status} /></TableCell>
      )}
      {showCol('tenant') && (
        <TableCell className="text-slate-700">
          <span className="font-medium text-sm">{lease.tenant_name}</span>
        </TableCell>
      )}
      {showCol('lease_rate') && (
        <TableCell className="text-right text-slate-600 font-mono text-sm">
          {formatCurrency(lease.lease_rate)}
        </TableCell>
      )}
      {showCol('trans_rev') && (
        <TableCell className="text-slate-400">{'\u2014'}</TableCell>
      )}
      {showCol('fuel_rev') && (
        <TableCell className="text-right text-amber-600 font-mono font-medium text-sm">
          {lease.fuel_revenue ? formatCurrency(lease.fuel_revenue, 0) : '\u2014'}
        </TableCell>
      )}
      {showCol('yield') && (
        <TableCell className="text-right font-bold text-slate-900 bg-slate-50/30 border-l border-r font-mono text-sm">
          {formatYieldPerSqft(lease.yield_per_sqft)}
        </TableCell>
      )}
      {showCol('total_value') && (
        <TableCell className="text-right text-slate-900 font-mono font-bold text-sm">
          {(() => {
            const total = lease.lease_rate + (lease.fuel_revenue || 0)
            return total > 0 ? formatCurrency(total, 0) : '\u2014'
          })()}
        </TableCell>
      )}
      {showCol('total_value_per_sqft') && (
        <TableCell className="text-right text-slate-900 font-mono font-medium text-sm">
          {(() => {
            const total = lease.lease_rate + (lease.fuel_revenue || 0)
            return lease.sqft > 0 && total > 0 ? formatYieldPerSqft(total / lease.sqft) : '\u2014'
          })()}
        </TableCell>
      )}
      {showCol('fuel_commitment') && (
        <TableCell>
          {lease.fuel_commitment_gallons && lease.fuel_commitment_gallons > 0 ? (
            <div className="flex items-center gap-2">
              <div className="w-10 h-2 bg-slate-200 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full ${
                    (lease.fuel_commitment_pct ?? 0) >= 90 ? 'bg-emerald-500' :
                    (lease.fuel_commitment_pct ?? 0) >= 60 ? 'bg-amber-400' : 'bg-red-400'
                  }`}
                  style={{ width: `${Math.min(100, lease.fuel_commitment_pct ?? 0)}%` }}
                />
              </div>
              <span className="text-xs font-mono text-amber-700">
                {lease.fuel_delivered_gallons ?? 0}/{lease.fuel_commitment_gallons}
              </span>
            </div>
          ) : (
            <span className="text-slate-400">{'\u2014'}</span>
          )}
        </TableCell>
      )}
      {showCol('contract_end') && (
        <TableCell className="text-slate-500 text-sm">
          {lease.lease_type === 'month_to_month' ? (
            <span className="text-purple-600 font-medium">M2M</span>
          ) : lease.lease_end ? (
            <div>
              {lease.lease_end}
              {lease.days_to_expiry !== undefined && lease.days_to_expiry <= 90 && (
                <div className="text-xs text-rose-500 font-medium flex items-center gap-1 mt-0.5">
                  <AlertCircle className="w-3 h-3" />
                  {lease.days_to_expiry} days
                </div>
              )}
            </div>
          ) : (
            '\u2014'
          )}
        </TableCell>
      )}
      {showCol('annual_value') && (
        <TableCell className="text-right text-slate-700 font-mono text-sm">
          {lease.annual_value > 0 ? formatCurrency(lease.annual_value, 0) : '\u2014'}
        </TableCell>
      )}
      {showCol('rate_gap') && (
        <TableCell className="text-right">
          {lease.rate_gap_pct != null ? (
            <span className={`font-mono font-medium text-sm ${
              lease.rate_gap_pct < 80 ? 'text-red-600' :
              lease.rate_gap_pct < 95 ? 'text-orange-600' :
              lease.rate_gap_pct > 110 ? 'text-emerald-600' : 'text-slate-700'
            }`}>
              {lease.rate_gap_pct.toFixed(0)}%
            </span>
          ) : '\u2014'}
        </TableCell>
      )}
      {showCol('vacancy_cost') && <TableCell className="text-slate-400">{'\u2014'}</TableCell>}
      {showCol('trans_rate_night') && <TableCell className="text-slate-400">{'\u2014'}</TableCell>}
      {showCol('utilization') && <TableCell className="text-slate-400">{'\u2014'}</TableCell>}
      {showCol('renewal') && (
        <TableCell>
          {lease.renewal_status && lease.renewal_status !== '\u2014' ? (
            <Badge variant="outline" className={`text-xs ${
              lease.renewal_status === 'Auto-Renew' ? 'bg-emerald-100 text-emerald-700' :
              lease.renewal_status === 'Contacted' ? 'bg-blue-100 text-blue-700' :
              lease.renewal_status === 'Not Started' ? 'bg-yellow-100 text-yellow-700' : ''
            }`}>
              {lease.renewal_status}
            </Badge>
          ) : (
            <span className="text-slate-400">{'\u2014'}</span>
          )}
        </TableCell>
      )}
      {showCol('days_to_expiry') && (
        <TableCell className="text-right">
          {lease.days_to_expiry != null ? (
            <span className={`font-mono text-sm ${
              lease.days_to_expiry <= 30 ? 'text-red-600 font-bold' :
              lease.days_to_expiry <= 60 ? 'text-orange-600 font-medium' :
              lease.days_to_expiry <= 90 ? 'text-yellow-600' : 'text-slate-600'
            }`}>
              {lease.days_to_expiry}
            </span>
          ) : (
            <span className="text-slate-400">{'\u2014'}</span>
          )}
        </TableCell>
      )}
      {showCol('escalation') && (
        <TableCell className="text-right">
          {lease.escalation_pct != null && lease.escalation_type !== 'none' ? (
            <span className="font-mono text-slate-700 text-sm">
              {lease.escalation_pct}% <span className="text-xs text-slate-400">({lease.escalation_type === 'cpi' ? 'CPI' : 'Fixed'})</span>
            </span>
          ) : (
            <span className="text-slate-400">{'\u2014'}</span>
          )}
        </TableCell>
      )}
    </TableRow>
  )
}

// ─── Transient Aggregate Sub-Row ────────────────────────────────────

function TransientAggSubRow({
  agg,
  showCol,
}: {
  agg: TransientAggRow
  showCol: (id: string) => boolean
}) {
  return (
    <TableRow className="hover:bg-blue-50/30">
      {showCol('unit') && (
        <TableCell className="pl-10 text-slate-500 text-sm italic">(aggregated)</TableCell>
      )}
      {showCol('sqft') && (
        <TableCell className="text-right text-slate-500 text-sm">
          {agg.sqft > 0 ? agg.sqft.toLocaleString() : '\u2014'}
        </TableCell>
      )}
      {showCol('status') && (
        <TableCell>
          <StatusBadge status="transient" />
        </TableCell>
      )}
      {showCol('tenant') && (
        <TableCell className="text-blue-600 font-medium text-sm">
          {agg.stay_count} stays
        </TableCell>
      )}
      {showCol('lease_rate') && (
        <TableCell className="text-slate-400">{'\u2014'}</TableCell>
      )}
      {showCol('trans_rev') && (
        <TableCell className="text-right text-blue-600 font-mono font-medium text-sm">
          {agg.total_revenue > 0 ? formatCurrency(agg.total_revenue, 0) : '\u2014'}
        </TableCell>
      )}
      {showCol('fuel_rev') && (
        <TableCell className="text-right text-amber-600 font-mono font-medium text-sm">
          {agg.fuel_revenue > 0 ? formatCurrency(agg.fuel_revenue, 0) : '\u2014'}
        </TableCell>
      )}
      {showCol('yield') && (
        <TableCell className="text-right font-bold text-slate-900 bg-slate-50/30 border-l border-r font-mono text-sm">
          {formatYieldPerSqft(agg.yield_per_sqft)}
        </TableCell>
      )}
      {showCol('total_value') && <TableCell className="text-slate-400">{'\u2014'}</TableCell>}
      {showCol('total_value_per_sqft') && <TableCell className="text-slate-400">{'\u2014'}</TableCell>}
      {showCol('fuel_commitment') && <TableCell className="text-slate-400">{'\u2014'}</TableCell>}
      {showCol('contract_end') && <TableCell className="text-slate-400">{'\u2014'}</TableCell>}
      {showCol('annual_value') && (
        <TableCell className="text-right text-slate-700 font-mono text-sm">
          {agg.total_revenue > 0 ? formatCurrency(agg.total_revenue * 12, 0) : '\u2014'}
        </TableCell>
      )}
      {showCol('rate_gap') && <TableCell className="text-slate-400">{'\u2014'}</TableCell>}
      {showCol('vacancy_cost') && <TableCell className="text-slate-400">{'\u2014'}</TableCell>}
      {showCol('trans_rate_night') && (
        <TableCell className="text-right text-blue-600 font-mono text-sm">
          {agg.trans_rate_per_night > 0 ? formatCurrency(agg.trans_rate_per_night, 0) : '\u2014'}
        </TableCell>
      )}
      {showCol('utilization') && (
        <TableCell>
          {agg.utilization_pct > 0 ? (
            <div className="flex items-center gap-2">
              <div className="w-12 h-2 bg-slate-200 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full ${
                    agg.utilization_pct >= 80 ? 'bg-emerald-500' :
                    agg.utilization_pct >= 50 ? 'bg-amber-400' : 'bg-red-400'
                  }`}
                  style={{ width: `${agg.utilization_pct}%` }}
                />
              </div>
              <span className="text-xs font-mono text-slate-600">{agg.utilization_pct}%</span>
            </div>
          ) : (
            <span className="text-slate-400">{'\u2014'}</span>
          )}
        </TableCell>
      )}
      {showCol('renewal') && <TableCell className="text-slate-400">{'\u2014'}</TableCell>}
      {showCol('days_to_expiry') && <TableCell className="text-slate-400">{'\u2014'}</TableCell>}
      {showCol('escalation') && <TableCell className="text-slate-400">{'\u2014'}</TableCell>}
    </TableRow>
  )
}

// ─── Unit Row ────────────────────────────────────────────────────────

function UnitRow({
  unit,
  showCol,
  indented,
}: {
  unit: ProcessedUnit
  showCol: (id: string) => boolean
  indented?: boolean
}) {
  return (
    <TableRow className="hover:bg-slate-50/50">
      {showCol('unit') && (
        <TableCell className={`font-medium text-slate-900 ${indented ? 'pl-10' : ''}`}>{unit.unit_code}</TableCell>
      )}
      {showCol('sqft') && (
        <TableCell className="text-right text-slate-600">{unit.sqft.toLocaleString()}</TableCell>
      )}
      {showCol('status') && (
        <TableCell><StatusBadge status={unit.status} /></TableCell>
      )}
      {showCol('tenant') && (
        <TableCell className="text-slate-700">
          <span className="font-medium">{unit.tenant_name || '\u2014'}</span>
        </TableCell>
      )}
      {showCol('lease_rate') && (
        <TableCell className="text-right text-slate-600 font-mono">
          {unit.lease_rate ? formatCurrency(unit.lease_rate) : '\u2014'}
        </TableCell>
      )}
      {showCol('trans_rev') && (
        <TableCell className="text-right text-blue-600 font-mono font-medium">
          {unit.transient_revenue ? formatCurrency(unit.transient_revenue, 0) : '\u2014'}
        </TableCell>
      )}
      {showCol('fuel_rev') && (
        <TableCell className="text-right text-amber-600 font-mono font-medium">
          {unit.fuel_revenue ? formatCurrency(unit.fuel_revenue, 0) : '\u2014'}
        </TableCell>
      )}
      {showCol('yield') && (
        <TableCell className="text-right font-bold text-slate-900 bg-slate-50/30 border-l border-r font-mono">
          {formatYieldPerSqft(unit.yield_per_sqft)}
        </TableCell>
      )}
      {showCol('total_value') && (
        <TableCell className="text-right text-slate-900 font-mono font-bold">
          {unit.total_tenant_value ? formatCurrency(unit.total_tenant_value, 0) : '\u2014'}
        </TableCell>
      )}
      {showCol('total_value_per_sqft') && (
        <TableCell className="text-right text-slate-900 font-mono font-medium">
          {unit.total_value_per_sqft ? formatYieldPerSqft(unit.total_value_per_sqft) : '\u2014'}
        </TableCell>
      )}
      {showCol('fuel_commitment') && (
        <TableCell>
          {unit.fuel_commitment_gallons ? (
            <div className="flex items-center gap-2">
              <div className="w-10 h-2 bg-slate-200 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full ${
                    (unit.fuel_commitment_pct ?? 0) >= 90 ? 'bg-emerald-500' :
                    (unit.fuel_commitment_pct ?? 0) >= 60 ? 'bg-amber-400' : 'bg-red-400'
                  }`}
                  style={{ width: `${Math.min(100, unit.fuel_commitment_pct ?? 0)}%` }}
                />
              </div>
              <span className="text-xs font-mono text-amber-700">
                {unit.fuel_delivered_gallons ?? 0}/{unit.fuel_commitment_gallons}
              </span>
            </div>
          ) : (
            <span className="text-slate-400">{'\u2014'}</span>
          )}
        </TableCell>
      )}
      {showCol('contract_end') && (
        <TableCell className="text-slate-500 text-sm">
          {unit.lease_type === 'month_to_month' ? (
            <span className="text-purple-600 font-medium">M2M</span>
          ) : unit.lease_end ? (
            <div>
              {unit.lease_end}
              {unit.days_to_expiry !== undefined && unit.days_to_expiry <= 90 && (
                <div className="text-xs text-rose-500 font-medium flex items-center gap-1 mt-0.5">
                  <AlertCircle className="w-3 h-3" />
                  {unit.days_to_expiry} days
                </div>
              )}
            </div>
          ) : (
            '\u2014'
          )}
        </TableCell>
      )}
      {showCol('annual_value') && (
        <TableCell className="text-right text-slate-700 font-mono">
          {unit.annual_value ? formatCurrency(unit.annual_value, 0) : '\u2014'}
        </TableCell>
      )}
      {showCol('rate_gap') && (
        <TableCell className="text-right">
          {unit.rate_gap_pct != null ? (
            <span className={`font-mono font-medium ${
              unit.rate_gap_pct < 80 ? 'text-red-600' :
              unit.rate_gap_pct < 95 ? 'text-orange-600' :
              unit.rate_gap_pct > 110 ? 'text-emerald-600' : 'text-slate-700'
            }`}>
              {unit.rate_gap_pct.toFixed(0)}%
            </span>
          ) : '\u2014'}
        </TableCell>
      )}
      {showCol('vacancy_cost') && (
        <TableCell className="text-right text-red-600 font-mono">
          {unit.vacancy_cost ? formatCurrency(unit.vacancy_cost, 0) : '\u2014'}
        </TableCell>
      )}
      {showCol('trans_rate_night') && (
        <TableCell className="text-right text-blue-600 font-mono">
          {unit.trans_rate_per_night ? formatCurrency(unit.trans_rate_per_night, 0) : '\u2014'}
        </TableCell>
      )}
      {showCol('utilization') && (
        <TableCell>
          {unit.utilization_pct != null ? (
            <div className="flex items-center gap-2">
              <div className="w-12 h-2 bg-slate-200 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full ${
                    unit.utilization_pct >= 80 ? 'bg-emerald-500' :
                    unit.utilization_pct >= 50 ? 'bg-amber-400' : 'bg-red-400'
                  }`}
                  style={{ width: `${unit.utilization_pct}%` }}
                />
              </div>
              <span className="text-xs font-mono text-slate-600">{unit.utilization_pct}%</span>
            </div>
          ) : (
            <span className="text-slate-400">{'\u2014'}</span>
          )}
        </TableCell>
      )}
      {showCol('renewal') && (
        <TableCell>
          {unit.renewal_status && unit.renewal_status !== '\u2014' ? (
            <Badge variant="outline" className={`text-xs ${
              unit.renewal_status === 'Auto-Renew' ? 'bg-emerald-100 text-emerald-700' :
              unit.renewal_status === 'Contacted' ? 'bg-blue-100 text-blue-700' :
              unit.renewal_status === 'Not Started' ? 'bg-yellow-100 text-yellow-700' : ''
            }`}>
              {unit.renewal_status}
            </Badge>
          ) : (
            <span className="text-slate-400">{'\u2014'}</span>
          )}
        </TableCell>
      )}
      {showCol('days_to_expiry') && (
        <TableCell className="text-right">
          {unit.days_to_expiry != null ? (
            <span className={`font-mono ${
              unit.days_to_expiry <= 30 ? 'text-red-600 font-bold' :
              unit.days_to_expiry <= 60 ? 'text-orange-600 font-medium' :
              unit.days_to_expiry <= 90 ? 'text-yellow-600' : 'text-slate-600'
            }`}>
              {unit.days_to_expiry}
            </span>
          ) : (
            <span className="text-slate-400">{'\u2014'}</span>
          )}
        </TableCell>
      )}
      {showCol('escalation') && (
        <TableCell className="text-right">
          {unit.escalation_pct != null && unit.escalation_type !== 'none' ? (
            <span className="font-mono text-slate-700">
              {unit.escalation_pct}% <span className="text-xs text-slate-400">({unit.escalation_type === 'cpi' ? 'CPI' : 'Fixed'})</span>
            </span>
          ) : (
            <span className="text-slate-400">{'\u2014'}</span>
          )}
        </TableCell>
      )}
    </TableRow>
  )
}

// ─── Grand Total Row ────────────────────────────────────────────────

function GrandTotalRow({
  allUnits,
  grandTotals,
  showCol,
}: {
  allUnits: ProcessedUnit[]
  grandTotals: { totalSqft: number; leaseTotal: number; transTotal: number; fuelTotal: number; totalTenantValue: number; avgYield: number; totalValuePerSqft: number; annualTotal: number; vacancyTotal: number }
  showCol: (id: string) => boolean
}) {
  return (
    <TableRow className="bg-slate-900 hover:bg-slate-900 text-white font-bold text-base border-t-2 border-slate-900">
      {showCol('unit') && (
        <TableCell className="pl-4 py-4">
          FACILITY TOTAL
          <span className="text-slate-400 font-normal text-sm ml-3">
            {allUnits.length} units
          </span>
        </TableCell>
      )}
      {showCol('sqft') && (
        <TableCell className="text-right py-4 font-mono text-slate-300">
          {grandTotals.totalSqft.toLocaleString()}
        </TableCell>
      )}
      {showCol('status') && <TableCell className="py-4" />}
      {showCol('tenant') && <TableCell className="py-4" />}
      {showCol('lease_rate') && (
        <TableCell className="text-right py-4 font-mono text-emerald-300">
          {formatCurrency(grandTotals.leaseTotal)}
        </TableCell>
      )}
      {showCol('trans_rev') && (
        <TableCell className="text-right py-4 font-mono text-blue-300">
          {formatCurrency(grandTotals.transTotal)}
        </TableCell>
      )}
      {showCol('fuel_rev') && (
        <TableCell className="text-right py-4 font-mono text-amber-300">
          {grandTotals.fuelTotal > 0 ? formatCurrency(grandTotals.fuelTotal) : '\u2014'}
        </TableCell>
      )}
      {showCol('yield') && (
        <TableCell className="text-right py-4 font-mono text-white bg-slate-800 border-l border-slate-700">
          {formatYieldPerSqft(grandTotals.avgYield)}
        </TableCell>
      )}
      {showCol('total_value') && (
        <TableCell className="text-right py-4 font-mono text-white font-bold">
          {grandTotals.totalTenantValue > 0 ? formatCurrency(grandTotals.totalTenantValue) : '\u2014'}
        </TableCell>
      )}
      {showCol('total_value_per_sqft') && (
        <TableCell className="text-right py-4 font-mono text-white">
          {grandTotals.totalValuePerSqft > 0 ? formatYieldPerSqft(grandTotals.totalValuePerSqft) : '\u2014'}
        </TableCell>
      )}
      {showCol('fuel_commitment') && <TableCell className="py-4" />}
      {showCol('contract_end') && <TableCell className="py-4" />}
      {showCol('annual_value') && (
        <TableCell className="text-right py-4 font-mono text-slate-300">
          {formatCurrency(grandTotals.annualTotal, 0)}
        </TableCell>
      )}
      {showCol('rate_gap') && <TableCell className="py-4" />}
      {showCol('vacancy_cost') && (
        <TableCell className="text-right py-4 font-mono text-red-300">
          {grandTotals.vacancyTotal > 0 ? formatCurrency(grandTotals.vacancyTotal, 0) : '\u2014'}
        </TableCell>
      )}
      {showCol('trans_rate_night') && <TableCell className="py-4" />}
      {showCol('utilization') && <TableCell className="py-4" />}
      {showCol('renewal') && <TableCell className="py-4" />}
      {showCol('days_to_expiry') && <TableCell className="py-4" />}
      {showCol('escalation') && <TableCell className="py-4" />}
    </TableRow>
  )
}
